import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { db } from '@/lib/db';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import { createLogger } from '@/lib/logger';

const log = createLogger('OfflineSync');

interface OfflineSyncContextValue {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncAt: Date | null;
  syncNow: () => Promise<void>;
}

const OfflineSyncContext = createContext<OfflineSyncContextValue | null>(null);

export function useOfflineSyncContext() {
  const context = useContext(OfflineSyncContext);
  if (!context) {
    throw new Error('useOfflineSyncContext must be used within OfflineSyncProvider');
  }
  return context;
}

interface OfflineSyncProviderProps {
  children: React.ReactNode;
}

export function OfflineSyncProvider({ children }: OfflineSyncProviderProps) {
  const { isAuthenticated } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);

  const syncInProgress = useRef(false);
  const wasOffline = useRef(false);

  // Mettre à jour le compteur
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await db.getPendingCount();
      setPendingCount(count);
    } catch (error) {
      log.warn('Erreur comptage pending', { error });
    }
  }, []);

  // Synchroniser les opérations en attente
  const syncNow = useCallback(async () => {
    if (syncInProgress.current || !navigator.onLine) {
      return;
    }

    syncInProgress.current = true;
    setIsSyncing(true);

    try {
      const queue = await db.syncQueue.orderBy('timestamp').toArray();

      if (queue.length === 0) {
        setLastSyncAt(new Date());
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const item of queue) {
        try {
          await processQueueItem(item);
          await db.syncQueue.delete(item.id!);
          successCount++;
        } catch (error) {
          errorCount++;

          // Mettre à jour le retry count
          const newRetryCount = item.retryCount + 1;
          if (newRetryCount >= 3) {
            // Trop de retries, abandonner
            await db.syncQueue.delete(item.id!);
          } else {
            await db.syncQueue.update(item.id!, {
              retryCount: newRetryCount,
              lastError: error instanceof Error ? error.message : 'Erreur inconnue',
            });
          }
        }
      }

      if (successCount > 0) {
        toast({
          title: 'Synchronisation réussie',
          description: `${successCount} modification${successCount > 1 ? 's' : ''} synchronisée${successCount > 1 ? 's' : ''}`,
          variant: 'success',
        });
      }

      if (errorCount > 0 && errorCount === queue.length) {
        toast({
          title: 'Erreur de synchronisation',
          description: 'Certaines modifications n\'ont pas pu être synchronisées',
          variant: 'destructive',
        });
      }

      setLastSyncAt(new Date());
    } catch {
      // Erreur sync globale - silencieuse
    } finally {
      syncInProgress.current = false;
      setIsSyncing(false);
      await updatePendingCount();
    }
  }, [updatePendingCount]);

  // Gérer les changements de connexion
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);

      // Si on était offline, synchroniser automatiquement
      if (wasOffline.current) {
        wasOffline.current = false;
        toast({
          title: 'Connexion rétablie',
          description: 'Synchronisation en cours...',
          variant: 'success',
        });

        // Attendre un peu que la connexion soit stable
        setTimeout(() => {
          syncNow();
        }, 1000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      wasOffline.current = true;

      toast({
        title: 'Mode hors ligne',
        description: 'Vos modifications seront sauvegardées localement',
        variant: 'default',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // État initial
    setIsOnline(navigator.onLine);
    if (!navigator.onLine) {
      wasOffline.current = true;
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncNow]);

  // Mettre à jour le compteur régulièrement
  useEffect(() => {
    if (!isAuthenticated) return;

    updatePendingCount();
    const interval = setInterval(updatePendingCount, 10000);

    return () => clearInterval(interval);
  }, [isAuthenticated, updatePendingCount]);

  // Sync automatique au démarrage si connecté et des opérations en attente
  useEffect(() => {
    if (isAuthenticated && isOnline && pendingCount > 0 && !syncInProgress.current) {
      syncNow();
    }
  }, [isAuthenticated, isOnline, pendingCount, syncNow]);

  const value: OfflineSyncContextValue = {
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncAt,
    syncNow,
  };

  return (
    <OfflineSyncContext.Provider value={value}>
      {children}
    </OfflineSyncContext.Provider>
  );
}

/**
 * Traiter un item de la queue
 */
async function processQueueItem(item: {
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  table: 'produits' | 'stocks';
  recordId: string;
  data: Record<string, unknown>;
}): Promise<void> {
  const { operation, table, recordId, data } = item;

  // Filtrer les champs valides
  const filteredData = filterDataForTable(table, data);

  switch (operation) {
    case 'INSERT': {
      const { error } = await (supabase.from(table) as any).insert(filteredData);
      if (error) throw error;
      break;
    }
    case 'UPDATE': {
      const { error } = await (supabase.from(table) as any).update(filteredData).eq('id', recordId);
      if (error) throw error;
      break;
    }
    case 'DELETE': {
      const { error } = await (supabase.from(table) as any).delete().eq('id', recordId);
      if (error) throw error;
      break;
    }
  }

  // Mettre à jour le cache local
  if (table === 'produits') {
    if (operation === 'DELETE') {
      await db.products.delete(recordId);
    } else {
      await db.products.update(recordId, { _syncStatus: 'synced' });
    }
  } else if (table === 'stocks') {
    if (operation === 'DELETE') {
      await db.stocks.delete(recordId);
    } else {
      await db.stocks.update(recordId, { _syncStatus: 'synced' });
    }
  }
}

/**
 * Filtrer les données pour ne garder que les champs valides
 */
function filterDataForTable(table: string, data: Record<string, unknown>): Record<string, unknown> {
  const VALID_FIELDS: Record<string, string[]> = {
    produits: [
      'id', 'restaurant_id', 'nom', 'categorie', 'unite', 'code_barre',
      'seuil_critique', 'seuil_alerte', 'cout_unitaire', 'fournisseur', 'taux_tva',
    ],
    stocks: [
      'id', 'restaurant_id', 'produit_id', 'zone', 'quantite',
      'derniere_mise_a_jour', 'updated_by',
    ],
  };

  const validFields = VALID_FIELDS[table] || [];
  const filtered: Record<string, unknown> = {};

  for (const field of validFields) {
    if (field in data && data[field] !== undefined) {
      filtered[field] = data[field];
    }
  }

  return filtered;
}
